import { FC } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import type { User } from '../../types';

const MessaggiRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;

  & .messaggi-unread-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: gold;
    flex-shrink: 0;
  }
`;

const Container = styled.div`
  position: absolute;
  right: 0;
  top: 25px;
  min-width: 300px;
  background-color: gray;
  color: white;
  z-index: 9999;

  & .font-order-type {
    color: white;
  }

  & .menu-icon:hover {
    cursor: pointer;
    color: gold;
  }
`;

const MenuContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px;
  border: 2px solid #f62753;

  & .section_menu_name {
    font-weight: 600;
    padding: 7px 10px;
  }

  & .section_menu_name:hover {
    cursor: pointer;
    background-color: #f62753;
  }
`;

const UserOverview = styled.div`
  padding: 10px 10px 12px;
  margin-bottom: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.25);

  & .user-overview-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 4px;
  }

  & .user-overview-name {
    font-weight: 600;
    font-size: 14px;
    color: white;
    margin: 0 0 2px 0;
    word-break: break-word;
  }

  & .user-overview-email {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    word-break: break-all;
  }
`;

interface MenuUserDesktopProps {
  user: User;
  handleLeaveMenu: () => void;
  handleLogOutClick: () => void;
  onNavigationStart?: () => void;
  unreadNotificationsCount?: number;
  onNotificationsRead?: () => void;
}

const MenuUserDesktop: FC<MenuUserDesktopProps> = ({
  user,
  handleLeaveMenu,
  handleLogOutClick,
  onNavigationStart,
  unreadNotificationsCount = 0,
  onNotificationsRead,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigationClick = (link: string): void => {
    const targetPath = link.startsWith('/') ? link : '/' + link;
    if (location.pathname === targetPath) {
      handleLeaveMenu();
      return;
    }
    if (onNavigationStart) {
      onNavigationStart();
    }
    if (link === 'messaggi' && onNotificationsRead) {
      onNotificationsRead();
    }
    navigate(link);
  };

  return (
    <Container onMouseLeave={() => handleLeaveMenu()}>
      <MenuContent>
        <UserOverview>
          <div className="user-overview-label">Il tuo account</div>
          <p className="user-overview-name">{user.name || user.username}</p>
          <p className="user-overview-email">{user.email}</p>
        </UserOverview>

        {/* Gestione PPV */}
        {!!user.is_admin && (
          <div
            className="section_menu_name"
            onClick={() => handleNavigationClick('admin/ppv/handle')}
          >
            <p>Gestisci PPVs (e match)</p>
          </div>
        )}

        {/* Gestione TypeMatch */}
        {!!user.is_admin && (
          <div
            className="section_menu_name"
            onClick={() => handleNavigationClick('admin/type-match/handle')}
          >
            <p>Gestisci TypeMatch</p>
          </div>
        )}

        {/* Gestione Arena */}
        {!!user.is_admin && (
          <div
            className="section_menu_name"
            onClick={() => handleNavigationClick('admin/arena/handle')}
          >
            <p>Gestisci Arena</p>
          </div>
        )}

        {/* Gestione Belts */}
        {!!user.is_admin && (
          <div
            className="section_menu_name"
            onClick={() => handleNavigationClick('admin/belt/handle')}
          >
            <p>Gestisci Cinture</p>
          </div>
        )}

        {/* Gestione Albo d'Oro */}
        {!!user.is_admin && (
          <div
            className="section_menu_name"
            onClick={() => handleNavigationClick('admin/hall-of-fame/handle')}
          >
            <p>Gestisci Albo d'Oro</p>
          </div>
        )}

        {/* Gestisci Wrestler */}
        {!!user.is_admin && (
          <div
            className="section_menu_name"
            onClick={() => handleNavigationClick('admin/wrestler/handle')}
          >
            <p>Gestisci Wrestler</p>
          </div>
        )}

        {/* Catalogo Carte */}
        {(!!user.is_founder || !!user.is_admin || !!user.is_super_admin) && (
          <div
            className="section_menu_name"
            onClick={() => handleNavigationClick('admin/catalog')}
          >
            <p>Catalogo Carte</p>
          </div>
        )}

        {/* Lista Utenti */}
        {!!user && (
          <div
            className="section_menu_name"
            onClick={() => handleNavigationClick('list-users')}
          >
            <p>Lista Utenti</p>
          </div>
        )}

        {/* Messaggi */}
        {!!user && (
          <div
            className="section_menu_name"
            onClick={() => handleNavigationClick('messaggi')}
          >
            <MessaggiRow>
              <p>Messaggi</p>
              {unreadNotificationsCount > 0 && (
                <span className="messaggi-unread-dot" title={`${unreadNotificationsCount} non lette`} />
              )}
            </MessaggiRow>
          </div>
        )}

        <button
          type="button"
          className="btn btn-danger d-inline-block"
          onClick={() => handleLogOutClick()}
        >
          Logout
        </button>
      </MenuContent>
    </Container>
  );
};

export default MenuUserDesktop;
